import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TodoIntegrationType } from '@demind-inc/core';
import { AxiosError } from 'axios';
import { useCallback, useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { TaskError401Res } from '../api';
import { taskErrorSnackBarAtom } from '../recoil';
import { FirestoreTaskItem } from '../types';

export interface SyncProviderTasksParams {
  userId: string;
  todoTasksBoardIds?: string[];
  fullSync?: boolean;
  refetchCount?: number;
}

interface SyncProviderRequestParams {
  userId: string;
  todoTasksBoardIds?: string[];
  fullSync?: boolean;
}

export type SyncProviderRequest = (
  params: SyncProviderRequestParams
) => Promise<FirestoreTaskItem[]>;

interface UseProviderTasksMutationOptions {
  onInvalidate?: () => void;
}

export const useProviderTasksMutation = (
  provider: TodoIntegrationType,
  request: SyncProviderRequest,
  options?: UseProviderTasksMutationOptions
): UseMutationResult<FirestoreTaskItem[], AxiosError, SyncProviderTasksParams> => {
  const queryClient = useQueryClient();
  const [, setTaskErrorSnackbar] = useRecoilState(taskErrorSnackBarAtom);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['lifestack.todo.tasks'] });
    queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
  }, [queryClient]);

  const mutation = useMutation<FirestoreTaskItem[], AxiosError, SyncProviderTasksParams>({
    mutationFn: async ({ userId, todoTasksBoardIds, fullSync = false }) =>
      request({ userId, todoTasksBoardIds, fullSync }),
    onSuccess: (data, variables) => {
      const refetchCount = variables?.refetchCount ?? 1;
      if (refetchCount === 1 && data.length > 0) {
        if (options?.onInvalidate) {
          options.onInvalidate();
        } else {
          invalidateQueries();
        }

        if (
          provider === 'ticktick' ||
          provider === 'trello' ||
          provider === 'clickup' ||
          provider === 'google-tasks'
        ) {
          queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
        }
      }
    },
  });

  useEffect(() => {
    const errorResponse = mutation.error?.response;
    if (!errorResponse) {
      return;
    }

    if (errorResponse.status === 401) {
      setTaskErrorSnackbar({
        status: errorResponse.status,
        message: 'Your connection expired — try reconnecting.',
        provider: ((errorResponse.data as TaskError401Res)?.provider ||
          provider) as TodoIntegrationType,
        action: 'sync',
      });
      return;
    }

    setTaskErrorSnackbar({
      status: errorResponse.status,
      message: "Couldn't sync that task.",
      provider: ((errorResponse.data as TaskError401Res)?.provider ||
        provider) as TodoIntegrationType,
      action: 'sync',
    });
  }, [mutation.error, provider, setTaskErrorSnackbar]);

  return mutation;
};
